import React from 'react';
import moment from 'moment';

import { store } from '../../store';
import connectWithStore from '../../utils/store';

import { fetchCaseDetail, postCaseAnnotation, postCaseResolve } from '../../api/cases/actions';
import { formatKennitala } from '../../utils';
import { updateCustomer } from '../../api/customers/actions';
import { validateEmail, validatePhoneNumber, formatMoney, formatNumber } from "../../utils";
import { CONTRACT_STATUS_WAITING, CONTRACT_STATUS_ACTIVE, CONTRACT_STATUS_INACTIVE } from '../../constants';

class AgentCaseDetails extends React.PureComponent {

    constructor(props) {
        super(props);

        this.state = {
            cellularPhone: '',
            phone: '',
            email: '',
            annotationMessage: '',
            customerSaved: false,
            errors: '',
            attachedFiles: [],
            isDragOver: false,
            showResolveDialog: false,
            resolveDialogDecision: null,
            resolveDialogMessage: '',
            resolveDialogComment: '',
            isResolving: false,
        };

        this.lastLoadedCaseId = null;
        this.fileInputRef = React.createRef();

        this.onContactChange = this.onContactChange.bind(this);
        this.onAnnotationMessageChange = this.onAnnotationMessageChange.bind(this);
        this.saveContact = this.saveContact.bind(this);
        this.postAnnotation = this.postAnnotation.bind(this);
        this.validateForm = this.validateForm.bind(this);
        this.resolveCase = this.resolveCase.bind(this);
        this.openResolveDialog = this.openResolveDialog.bind(this);
        this.closeResolveDialog = this.closeResolveDialog.bind(this);
        this.confirmResolveCase = this.confirmResolveCase.bind(this);
        this.onResolveDialogCommentChange = this.onResolveDialogCommentChange.bind(this);
        this.onAttachClick = this.onAttachClick.bind(this);
        this.onFileInputChange = this.onFileInputChange.bind(this);
        this.onMessageEntryDragOver = this.onMessageEntryDragOver.bind(this);
        this.onMessageEntryDragLeave = this.onMessageEntryDragLeave.bind(this);
        this.onMessageEntryDrop = this.onMessageEntryDrop.bind(this);
    }

    componentDidMount() {
        this.loadCaseDetailIfNeeded();
        this.syncCutsomerContactFromData(null);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.caseId !== this.props.caseId || prevProps.match !== this.props.match) {
            this.loadCaseDetailIfNeeded();
        }

        if (prevProps.caseDetails !== this.props.caseDetails || prevProps.caseId !== this.props.caseId) {
            this.syncCutsomerContactFromData(prevProps);
        }
    }

    getActiveCaseId(props = this.props) {
        if (props && props.caseId) {
            return props.caseId;
        }

        const routeParams = props && props.match && props.match.params ? props.match.params : {};
        return routeParams.caseId || null;
    }

    loadCaseDetailIfNeeded() {
        const caseId = this.getActiveCaseId();
        if (!caseId) {
            return;
        }

        const currentCase = this.getCurrentCase();
        const currentCaseId = currentCase && (currentCase.CaseId || currentCase.caseId || currentCase.Id);

        if (String(currentCaseId || '') === String(caseId)) {
            return;
        }

        if (String(this.lastLoadedCaseId || '') === String(caseId)) {
            return;
        }

        this.lastLoadedCaseId = caseId;
        this.props.dispatch(fetchCaseDetail(caseId));
    }

    getCurrentCase() {
        if (!(this.props.caseDetails && this.props.caseDetails.case)) {
            return null;
        }
        return this.props.caseDetails.case || null
    }

    getCurrentContract() {
        if (!(this.props.caseDetails && this.props.caseDetails.contract)) {
            return null;
        }
        return this.props.caseDetails.contract || null;
    }

    getCurrentCustomer() {
        if (!(this.props.caseDetails && this.props.caseDetails.customer)) {
            return null;
        }
        return this.props.caseDetails.customer || null;
    }

    getContractId(caseItem, contract) {
        if (contract) {
            return contract.ContractId || contract.contractId || null;
        }
        if (caseItem) {
            return caseItem.ContractId || caseItem.contractId || null;
        }
        return null;
    }

    getPrimaryKennitala(caseItem) {
        if (!caseItem) {
            return null;
        }

        if (caseItem.Name && typeof caseItem.Name === 'string') {
            const match = caseItem.Name.match(/^(\d{10})\s*-/);
            if (match && match[1]) {
                return match[1];
            }
        }

        return null;
    }

    getPrimaryCustomerName(caseItem) {
        if (!caseItem) {
            return '<name-missing>';
        }

        if (caseItem.Name && typeof caseItem.Name === 'string') {
            const splitName = caseItem.Name.split(' - ');
            if (splitName.length > 1 && splitName[1]) {
                return splitName[1];
            }
            return caseItem.Name;
        }
        return '<name-missing>';
    }

    getCreatedMoment(caseItem) {
        if (!caseItem) {
            return null;
        }

        const createdValue = caseItem.CreatedDate || caseItem.created_at;
        if (moment.isMoment(createdValue)) {
            return createdValue;
        }
        if (!createdValue) {
            return null;
        }

        const parsed = moment.utc(createdValue, ['DD.MM.YYYY', 'YYYY-MM-DD', moment.ISO_8601], true);
        if (parsed.isValid()) {
            return parsed;
        }

        const fallback = moment.utc(createdValue);
        return fallback.isValid() ? fallback : null;
    }

    getInitials(value = '') {
        const upperCaseLetters = String(value).match(/[A-ZÁÉÍÓÚÝÞÆÖÐ]/g);
        if (upperCaseLetters && upperCaseLetters.length) {
            return upperCaseLetters.join('');
        }

        return String(value)
            .trim()
            .split(/\s+/)
            .filter(Boolean)
            .map(part => part.charAt(0).toUpperCase())
            .join('');
    }

    syncCutsomerContactFromData(prevProps) {
        const currentCase = this.getCurrentCase();

        const prevCustomer = prevProps && prevProps.caseDetails && prevProps.caseDetails.data ? (prevProps.caseDetails.customer) : null;
        const currentCustomer = this.getCurrentCustomer();

        const prevAccountNum = (prevCustomer && prevCustomer.AccountNum ) || null; 
        const currentAccountNum = (currentCustomer && currentCustomer.AccountNum) || null;

        if (!prevProps || String(prevAccountNum || '') !== String(currentAccountNum || '')) {
            this.setState({
                cellularPhone: (currentCustomer && (currentCustomer.CellularPhone)) || '', 
                phone: (currentCustomer && (currentCustomer.Phone)) || '',
                email: (currentCustomer && (currentCustomer.Email))  || '',
                customerSaved: false,
            });
        }
    }

    onContactChange(e) {
        const name = e.target.name;
        const value = e.target.value;
        this.setState({ [name]: value, customerSaved: false });
    }

    onAnnotationMessageChange(e) {
        this.setState({ annotationMessage: e.target.value });
    }

    onAttachClick(e) {
        if (e) {
            e.preventDefault();
        }

        if (this.fileInputRef && this.fileInputRef.current) {
            this.fileInputRef.current.click();
        }
    }

    onFileInputChange(e) {
        const files = Array.from((e.target && e.target.files) || []);
        this.setState({ attachedFiles: files });

        // Reset to allow selecting the same file again.
        if (e.target) {
            e.target.value = '';
        }
    }

    onMessageEntryDragOver(e) {
        e.preventDefault();
        e.stopPropagation();

        if (!this.state.isDragOver) {
            this.setState({ isDragOver: true });
        }
    }

    onMessageEntryDragLeave(e) {
        e.preventDefault();
        e.stopPropagation();
        this.setState({ isDragOver: false });
    }

    onMessageEntryDrop(e) {
        e.preventDefault();
        e.stopPropagation();

        const files = Array.from((e.dataTransfer && e.dataTransfer.files) || []);
        this.setState({
            attachedFiles: files,
            isDragOver: false,
        });
    }

    readFileAsDataUrl(fileObject) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => reject(new Error('Failed to read attachment file'));
            reader.readAsDataURL(fileObject);
        });
    }

    prepareAttachedFiles(files) {
        const fileList = Array.isArray(files) ? files : [];
        return Promise.all(fileList.map((file) => {
            if (file && file.base64) {
                return Promise.resolve({
                    ...file,
                    type: file.type || (file.file && file.file.type) || '',
                    name: file.name || (file.file && file.file.name) || 'attachment',
                });
            }

            const rawFile = (file && file.file) || file;
            return this.readFileAsDataUrl(rawFile).then((dataUrl) => ({
                ...file,
                base64: dataUrl,
                type: (file && file.type) || (rawFile && rawFile.type) || '',
                name: (file && file.name) || (rawFile && rawFile.name) || 'attachment',
            }));
        }));
    }

    validateForm() {
        let validateFunction = {};
        let errors = "";
        let valid = true;
        for (const key of Object.keys(this.state)) {
            let errorString = '';
            if (key === 'phone' || key === 'cellularPhone'){
                validateFunction = validatePhoneNumber;
                errorString = 'Símanúmer er ekki gilt';
            } else if (key === 'email') {
                validateFunction = validateEmail;
                errorString = 'Netfang er ekki gilt';
            }
            if (errorString !== '') {
                valid = valid && validateFunction(this.state[key]);
                errors += valid ? '' : errorString+', ';
            }
        }

        this.setState({ errors: errors });
        return valid;
    }
    

    saveContact(e) {
        e.preventDefault();
        if (this.validateForm()) {
            let customerId = this.getPrimaryKennitala(this.getCurrentCase());
            Promise.resolve(this.props.dispatch(updateCustomer(customerId, { 
                phone: this.state.phone, 
                cellular_phone: this.state.cellularPhone, 
                email: this.state.email 
            }))).then(() => {
                this.setState({ customerSaved: true });
            })
        }
    }

    postAnnotation(e) {
        if (e) {
            e.preventDefault();
        }

        const message = (this.state.annotationMessage || '').trim();
        if (!message) {
            return Promise.resolve();
        }

        const caseItem = this.getCurrentCase();
        if (!caseItem) {
            return Promise.resolve();
        }

        const caseId = caseItem.CaseId || caseItem.caseId;
        if (!caseId) {
            return Promise.resolve();
        }

        const advisorName = this.props.advisorFullName;
        const attachedFiles = this.state.attachedFiles || [];

        return this.prepareAttachedFiles(attachedFiles)
            .then((preparedFiles) => Promise.resolve(
                this.props.dispatch(postCaseAnnotation(caseId, 'Frá ráðgjafa', message, advisorName, preparedFiles))
            ))
            .then(() => {
                this.setState({ 
                    annotationMessage: '', 
                    attachedFiles: []
                });
            });
    }

    resolveCase(terminationDecision = null) {
        const caseItem = this.getCurrentCase();
        if (!caseItem) {
            return Promise.resolve();
        }

        const caseId = caseItem.CaseId || caseItem.caseId;
        const advisorCode = caseItem.AdvisorCode || caseItem.advisorCode || null;

        if (!caseId || !advisorCode) {
            return Promise.resolve();
        }
        
        return Promise.resolve(this.props.dispatch(postCaseResolve(caseId, advisorCode, terminationDecision, this.state.resolveDialogComment)))
            .then(() => this.props.history.push("/cases/"));
    }

    openResolveDialog(terminationDecision = null, message = 'Þú ert að fara að ljúka máli') {
        this.setState({
            showResolveDialog: true,
            resolveDialogDecision: terminationDecision,
            resolveDialogMessage: message,
            resolveDialogComment: '',
        });
        setTimeout(() => document.getElementById("resolve-modal-textarea").focus(),10);
    }

    closeResolveDialog() {
        this.setState({
            showResolveDialog: false,
            resolveDialogDecision: null,
            resolveDialogMessage: '',
            resolveDialogComment: '',
        });
    }

    onResolveDialogCommentChange(e) {
        this.setState({ resolveDialogComment: e.target.value });
    }

    confirmResolveCase(e) {
        if (e) {
            e.preventDefault();
        }

        if (this.state.isResolving) {
            return Promise.resolve();
        }

        this.setState({ isResolving: true });

        return Promise.resolve(this.resolveCase(this.state.resolveDialogDecision))
            .then(
                (result) => {
                    this.setState({ isResolving: false });
                    return result;
                },
                (error) => {
                    this.setState({ isResolving: false });
                    throw error;
                },
            );
    }

    renderTimelineItems(caseItem) {
        const annotations = (this.props.caseDetails && this.props.caseDetails.annotations) || [];

        const items = annotations.map((annotation, idx) => {
            const timeValue = annotation.CreatedDate;
            const parsed = timeValue ? moment.utc(timeValue, ['DD.MM.YYYY', 'YYYY-MM-DD', moment.ISO_8601], true) : null;
            const fallback = timeValue ? moment.utc(timeValue) : null;
            const createdAt = parsed && parsed.isValid() ? parsed : (fallback && fallback.isValid() ? fallback : null);
            const time = createdAt ? createdAt.format('HH:mm') : '-';
            const dateKey = createdAt ? createdAt.format('YYYY-MM-DD') : 'unknown-date';
            const dateLabel = createdAt ? createdAt.clone().locale('is').format('D. MMMM YYYY') : 'Óþekkt dagsetning';
            let advisorFullName = annotation.CreatedBy;
            let text = annotation.Description || '';
            const separatorIndex = text.indexOf(':');
            if (separatorIndex !== -1) {
                advisorFullName = text.substr(0, separatorIndex).trim();
                text = text.substr(separatorIndex + 1).trim();
            }
            const advisorInitials = this.getInitials(advisorFullName);

            return {
                key: idx,
                type: annotation.Title,
                advisorFullName,
                advisorInitials,
                text,
                createdAt,
                time,
                dateKey,
                dateLabel,
            };
        });

        if (items.length === 0) {
            return <div className="text-muted">Engar athugasemdir.</div>;
        }

        const groupedByDate = items.reduce((acc, item) => {
            if (!acc[item.dateKey]) {
                acc[item.dateKey] = {
                    dateKey: item.dateKey,
                    dateLabel: item.dateLabel,
                    items: [],
                };
            }

            acc[item.dateKey].items.push(item);
            return acc;
        }, {});

        const orderedGroups = Object.values(groupedByDate)
            .sort((a, b) => {
                if (a.dateKey === 'unknown-date' && b.dateKey === 'unknown-date') {
                    return 0;
                }
                if (a.dateKey === 'unknown-date') {
                    return 1;
                }
                if (b.dateKey === 'unknown-date') {
                    return -1;
                }
                return moment.utc(b.dateKey).valueOf() - moment.utc(a.dateKey).valueOf();
            })
            .map(group => ({
                ...group,
                items: group.items.sort((a, b) => {
                    if (!a.createdAt && !b.createdAt) {
                        return 0;
                    }
                    if (!a.createdAt) {
                        return 1;
                    }
                    if (!b.createdAt) {
                        return -1;
                    }
                    return b.createdAt.valueOf() - a.createdAt.valueOf();
                }),
            }));

        return (
            <div className="xs-mb-0">
                {orderedGroups.map((group, groupIdx) => (
                    <div key={`timeline-group-${group.dateKey}-${groupIdx}`} className="xs-mb-20">
                        <div className="timeline-date-separator xs-mb-15">
                            <span>{group.dateLabel}</span>
                        </div>

                        <ul className="list-unstyled xs-mb-0 timeline-list">
                            {group.items.map((item, idx) => (
                                <li key={`timeline-item-${item.key}-${idx}`} className="xs-mb-15 timeline-item">
                                    <div className="timeline-avatar" title={item.advisorFullName}>{item.advisorInitials || '•'}</div>
                                    <div className="card box-background timeline-item-card">
                                        <div className="card-body">
                                            <div className="row" style={{ justifyContent: "space-between"}}><div>{item.type}</div><div className="text-muted">{item.time}</div></div>
                                            <div>
                                            {String(item.text)
                                                .split(/<br\s*\/?>|\n/g)
                                                .map((part, i, arr) => (
                                                <React.Fragment key={i}>
                                                    {part}
                                                    {i < arr.length - 1 && <br />}
                                                </React.Fragment>
                                                ))}
                                            </div>                                            
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
        );
    }

    renderContactInfo(contract) {
        if (!contract) {
            return null;
        }
        let contractName = contract.ContractName || '';
        contractName = contractName.lastIndexOf(',') != -1 ? contractName.substr(0,contractName.lastIndexOf(',')) : contractName;

        const elements = [];
        const type = contract.ContractType;

        if (['VID','VIDT','VIDE'].includes(type)) {
            elements.push(<h6 key="title" className="card-title">Iðgjöld</h6>);
            elements.push(<hr key="divider"/>);
            elements.push(
                <div key="employer">
                    <div className="case-label">Launagreiðandi</div>
                    {contract.EmployerName || '-'}
                </div>
            );
            elements.push(
                <div key="contribution">
                    <div className="case-label">% Framlag skv. samningi</div>
                    {(formatNumber(contract.EmployeeContributionPct,2) + ' / ' + formatNumber(contract.EmployerContributionPct,2)) || '-'}
                </div>
            );
            elements.push(
                <div key="last-declaration">
                    <div className="case-label">Síðasta skilagrein barst</div>
                    {contract.LastDeclarationPayment || '-'}
                </div>
            );
            elements.push(
                <div key="last-germany-payment">
                    <div className="case-label">Síðasta iðgjald  til Þýskalands</div>
                    {contract.LastDeclarationPayment || '-'}
                </div>
            );
        } else {
            elements.push(<h6 key="title" className="card-title">{contractName}</h6>);
            elements.push(<hr key="divider"/>);

            if (["REN", "FOR", "RENE", "FORE"].includes(type)) {
                elements.push(
                    <div key="contract-years">
                        <div className="case-label">Samningsárafjöldi</div>
                        {contract.ContractYears || '-'}
                    </div>
                );
                elements.push(
                    <div key="premium">
                        <div className="case-label">Iðgjald</div>
                        {(contract.PremiumAmountCur && formatMoney(contract.PremiumAmountCur, 2)) || '-'}€
                    </div>
                );
            } else if (["RISLEB", "LEB"].includes(type)) {
                elements.push(
                    <div key="premium">
                        <div className="case-label">Iðgjald</div>
                        {(contract.PremiumAmountCur && formatMoney(contract.PremiumAmountCur, 2)) || '-'}€
                    </div>
                );
                elements.push(
                    <div key="assurance">
                        <div className="case-label">Líftrygging</div>
                        {(contract.AssuranceAmountCur && formatMoney(contract.AssuranceAmountCur)) || '-'}€
                    </div>
                );
                elements.push(
                    <div key="accidental-death">
                        <div className="case-label">Dánarbætur v. slyss</div>
                        {(contract.LifeInsuranceAmountCur && formatMoney(contract.LifeInsuranceAmountCur)) || '-'}€
                    </div>
                );
            } else if (["RIS", "RISU"].includes(type)) {
                elements.push(
                    <div key="contract-years">
                        <div className="case-label">Samningsárafjöldi</div>
                        {contract.ContractYears || '-'}
                    </div>
                );
                elements.push(
                    <div key="premium">
                        <div className="case-label">Iðgjald</div>
                        {(contract.PremiumAmountCur && formatMoney(contract.PremiumAmountCur, 2)) || '-'}€
                    </div>
                );
                elements.push(
                    <div key="disability">
                        <div className="case-label">Grunnörorkubætur</div>
                        {(contract.DisabilityInsuranceAmountCur && formatMoney(contract.DisabilityInsuranceAmountCur)) || '-'}€
                    </div>
                );
                elements.push(
                    <div key="accidental-death">
                        <div className="case-label">Dánarbætur v. slyss</div>
                        {(contract.LifeInsuranceAmountCur && formatMoney(contract.LifeInsuranceAmountCur)) || '-'}€
                    </div>
                );
            } else if (["KSP"].includes(type)) {
                elements.push(
                    <div key="contract-years">
                        <div className="case-label">Samningsárafjöldi</div>
                        {contract.ContractYears || '-'}
                    </div>
                );
                elements.push(
                    <div key="premium">
                        <div className="case-label">Iðgjald</div>
                        {(contract.PremiumAmountCur && formatMoney(contract.PremiumAmountCur, 2)) || '-'}€
                    </div>
                );
            }

        } 
        if (contract.Alternatives ) {
            elements.push(
                <div key="alternative">
                    <div className="case-label">Frávik</div>
                    {contract.Alternatives}
                </div>
            );
        }

        return elements.length ? <>{elements}</> : null;
    }
    

    render() {
        if (this.props.caseDetails && this.props.caseDetails.isFetching) {
            return (
                <div className="container-fluid xs-pt-30 xs-pb-50">
                    <div className="alert alert-info">Sæki mál...</div>
                </div>
            );
        }

        const caseItem = this.getCurrentCase();
        const contract = this.getCurrentContract();
        
        if (!caseItem) {
            return (
                <div className="container-fluid xs-pt-30 xs-pb-50">
                    <div className="alert alert-warning">Mál fannst ekki.</div>
                </div>
            );
        }

        const contractId = this.getContractId(caseItem, contract);

        const customerName = this.getPrimaryCustomerName(caseItem);
        const kennitala = this.getPrimaryKennitala(caseItem);
        const createdMoment = this.getCreatedMoment(caseItem);
        const subCategory = caseItem.SubCategoryText || caseItem.SubCategory || '-';
        const category = caseItem.CategoryText || caseItem.Category || '-';
        const canPostAnnotation = (this.state.annotationMessage || '').trim().length > 0;
        const contractFilesList = this.props.caseDetails.contractFilesList ? this.props.caseDetails.contractFilesList : [];

        const dueMoment = createdMoment ? moment.utc(createdMoment).add(10, 'days') : null;

        return (<>
            <style>{`
                .badge {
                    padding:10px;
                }
                .badge-type {
                    color: #003781;
                    background-color: #E8EEF8;
                }
                .badge-created {
                    color: #4B5563;
                    background-color: #F0F0F0;
                }
                .badge-due {
                    color: #92400E;
                    background-color: #FEF3C7;

                }
                .case-label {
                    text-transform: uppercase;
                    font-size: smaller;
                }
                .customer-name {
                    font-weight: bold;
                    font-size: larger;
                }
                h6.card-title {
                    font-variant: small-caps;
                }
                h6.bold {
                    font-weight: bold;
                }
                input.input-width {
                    width: 90%;
                }
                .timeline-date-separator {
                    display: flex;
                    align-items: center;
                    text-transform: uppercase;
                    color: #6c757d;;
                    white-space: nowrap;
                }
                .timeline-date-separator::before,
                .timeline-date-separator::after {
                    content: '';
                    flex: 1;
                    border-bottom: 1px solid #dee2e6;
                }
                .timeline-date-separator span {
                    padding: 0 10px;
                }
                .timeline-list {
                    position: relative;
                    padding: 0;
                }
                .timeline-list::before {
                    content: '';
                    position: absolute;
                    left: 20px;
                    top: 0;
                    bottom: 0;
                    width: 3px;
                    background-color: #E1E7EF;
                }
                .timeline-item {
                    display: flex;
                    align-items: flex-start;
                    gap: 12px;
                    position: relative;
                }
                .timeline-avatar {
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    background-color: #003781;
                    color: #fff;
                    font-weight: 700;
                    font-size: 14px;
                    line-height: 1;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    text-transform: uppercase;
                    flex-shrink: 0;
                    position: relative;
                    z-index: 1;
                }
                .timeline-item-card {
                    flex: 1;
                    border: 1px solid #E1E7EF;
                    border-radius: 12px;
                }
                .box-background {
                    background-color: #F9FAFB;
                }
                .message-entry,
                .message-enry {
                    display: flex;
                    align-items: center;
                    width: 100%;
                    padding: 8px 10px;
                    border: 1px solid #d9dee6;
                    border-radius: 7px;
                    background: #fff;
                    margin-bottom: 14px;
                }
                .message-entry:focus-within,
                .message-enry:focus-within {
                    border-color: #b7c1d1;
                }
                .message-entry.drag-over,
                .message-enry.drag-over {
                    border-color: #003781;
                    background: #F1F5FB;
                }
                .message-entry-input {
                    border: 0;
                    outline: none;
                    flex: 1;
                    padding: 2px 8px;
                    background: transparent;
                }
                .message-entry-icon,
                .message-entry-action {
                    border: 0;
                    background: transparent;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 26px;
                    height: 26px;
                    color: #818b99;
                    padding: 0;
                }
                .message-entry-actions {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                }
                .message-entry-action.send {
                    color: #0f3d8f;
                }
                .btn.bigger {
                    width: 35%;
                }
                .resolve-modal-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(0, 0, 0, 0.35);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 2000;
                    padding: 20px;
                }
                .resolve-modal {
                    background: #fff;
                    border-radius: 10px;
                    width: 100%;
                    max-width: 520px;
                    padding: 20px;
                    position: relative;
                    box-shadow: 0 10px 35px rgba(0, 0, 0, 0.2);
                }
                .resolve-modal-close {
                    position: absolute;
                    top: 10px;
                    right: 12px;
                    border: 0;
                    background: transparent;
                    font-size: 22px;
                    line-height: 1;
                    color: #6c757d;
                    padding: 0;
                }
                .resolve-modal-message {
                    margin-bottom: 12px;
                    padding-right: 18px;
                    font-weight: 600;
                }
                .resolve-modal-textarea {
                    width: 100%;
                    min-height: 110px;
                    border: 1px solid #d9dee6;
                    border-radius: 7px;
                    padding: 10px;
                    resize: vertical;
                    margin-bottom: 14px;
                }
            `}</style>        

            <div className="container xs-pt-20 xs-pb-50">
                <div className="row align-items-start align-items-md-center" style={{marginBottom: "20px"}}>
                    <div className="col-12 col-md-5 xs-mb-10 xs-mb-md-0">
                        <div className="font-weight-bold"><a href="/#/cases/">Mín mál</a> &gt; {subCategory}</div>
                    </div>
                    <div className="col-12 col-md-7 text-md-right">
                        { 
                        contract && category.toUpperCase()==='UPPSÖGN' 
                        ? <>
                        <button id="confirm-termination" onClick={() => this.openResolveDialog(857540001)} className="btn bigger btn-danger btn-sm xs-mr-10 xs-mb-5">Staðfesta uppsögn</button>
                        <button id="cancel-termination" onClick={() => this.openResolveDialog(857540000)} className="btn bigger btn-primary btn-sm xs-mb-5 cancel-termination">Uppsögn afturkölluð</button>
                        </>
                        :
                        <button id="case-resolve" onClick={() => this.openResolveDialog(null)} className="btn bigger btn-primary btn-sm xs-mr-10 xs-mb-5">Klára mál</button>
                        }
                    </div>
                </div>

                <div className="row">
                    <div className="col-12 xs-mb-20">
                        <div className="card">
                            <div className="row d-flex align-items-center card-body">
                                <div>
                                    <div className="case-label">Frávik #{caseItem.CaseId || '-'}</div>
                                    <div className="customer-name"><strong>{customerName}</strong></div>
                                    <div ><strong>Samningskenni:</strong> {contractId || '-'}</div>
                                    <div ><strong>Kennitala:</strong> {kennitala ? formatKennitala(kennitala) : '-'}</div>
                                </div>
                                <div className="xs-mt-5" style={{ marginLeft: 'auto', display: 'grid', rowGap: '10px'}}>
                                    <div className="badge badge-type">{category+' - '+subCategory}</div>
                                    <div className="badge badge-created"><strong>Stofnað</strong> {createdMoment ? createdMoment.format('D.M.YYYY') : '-'}</div>
                                    <div className="badge badge-due"><strong>Lokið</strong> {dueMoment ? dueMoment.format('D.M.YYYY') : '-'}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-12 col-lg-8">
                        <div className="card xs-mb-20">
                            <div className="card-body">
                                <h6 className="card-title bold">Verklýsing</h6>
                                <p className="xs-mb-10">{caseItem.Description || caseItem.Name || 'Engin lýsing tiltæk.'}</p>
                                {caseItem.CustodianName
                                ?
                                <>
                                    <hr/>
                                    <div>Vörsluaðili: <strong>{caseItem.CustodianName}</strong></div>
                                </>                                    
                                :<></>
                                }
                                {caseItem.OriginOfDismissal
                                ?
                                <>
                                    <hr/>
                                    <div>Uppruni uppsagnar: <strong>{caseItem.OriginOfDismissal}</strong></div>
                                </>                                    
                                :<></>
                                }                                
                                <hr/>
                                <div>Tengiliður hjá Allianz <a href={`mailto:${caseItem.OwnerEmail || '-'}`}>{caseItem.OwnerName || '-' }</a></div>
                            </div>
                        </div>

                        <div className="card xs-mb-20">
                            <div className="card-body">
                                <form onSubmit={this.saveContact}>
                                    <div className="row d-flex align-items-center">
                                        <h6 className="card-title bold xs-mb-0">Tengslaupplýsingar</h6>
                                        <input type="submit" className="btn btn-primary btn-sm" style={{ marginLeft: 'auto' }} value="Vista"/>
                                    </div>
                                    <div className="row" style={{marginTop: "20px"}} >
                                        <div style={{width:"25%"}} className="form-group">
                                            <label className="focused">Farsímanúmer</label>
                                            <input type="text" className="form-control input-width" name="cellularPhone" value={this.state.cellularPhone} onChange={this.onContactChange} />
                                        </div>
                                        <div style={{width:"25%"}} className="form-group">
                                            <label className="focused">Símanúmer</label>
                                            <input  type="text" className="form-control input-width" name="phone" value={this.state.phone} onChange={this.onContactChange} />
                                        </div>

                                        <div style={{width:"50%"}}  className="form-group">
                                            <label className="focused">Netfang</label>
                                            <input type="email" className="form-control input-width" name="email" value={this.state.email} onChange={this.onContactChange} />
                                        </div>
                                        
                                        {this.state.customerSaved && <span className="text-success xs-ml-10">Vistað</span>}
                                    </div>
                                </form>
                            </div>
                        </div>

                        <div className="card xs-mb-20">
                            <div className="card-body">
                                <h6 className="card-title bold">Tímalína</h6>
                                <form className={`message-entry message-enry ${this.state.isDragOver ? 'drag-over' : ''}`} onSubmit={this.postAnnotation} onDragOver={this.onMessageEntryDragOver} onDragLeave={this.onMessageEntryDragLeave} onDrop={this.onMessageEntryDrop}>
                                    <input ref={this.fileInputRef} type="file" style={{ display: 'none' }} onChange={this.onFileInputChange} multiple />
                                    <button type="button" className="message-entry-icon" aria-label="Edit message" onClick={this.postAnnotation} disabled={!canPostAnnotation || (this.props.caseDetails && this.props.caseDetails.isPostingAnnotation)}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                            <path d="M3 17.25V21h3.75L18.81 8.94l-3.75-3.75L3 17.25z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M14.06 4.19l3.75 3.75" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </button>

                                    <input className="message-entry-input" type="text" placeholder="Skráðu skilaboð..." value={this.state.annotationMessage} onChange={this.onAnnotationMessageChange} />

                                    <div className="message-entry-actions">
                                        <button type="button" className="message-entry-action" aria-label="Attach file" onClick={this.onAttachClick}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                                <path d="M21.44 11.05l-8.49 8.49a5 5 0 01-7.07-7.07l9.19-9.2a3.5 3.5 0 114.95 4.95l-9.2 9.19a2 2 0 11-2.82-2.83l8.48-8.48" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        </button>
                                        <button type="button" className="message-entry-action send" aria-label="Send message" onClick={this.postAnnotation} disabled={!canPostAnnotation || (this.props.caseDetails && this.props.caseDetails.isPostingAnnotation)}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                                <path d="M22 2L11 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                                <path d="M22 2L15 22l-4-9-9-4 20-7z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        </button>
                                    </div>
                                </form>
                                {this.state.attachedFiles.length > 0 && (
                                    <div className="text-muted xs-mb-10">
                                        Skrá(r) valdar: {this.state.attachedFiles.map(file => file.name).join(', ')}
                                    </div>
                                )}

                                {this.renderTimelineItems(caseItem)}
                            </div>
                        </div>
                    </div>

                    <div className="col-12 col-lg-4">
                        <div className="card xs-mb-20 box-background">
                            <div className="card-body">
                                { this.renderContactInfo(contract)}
                            </div>
                        </div>

                        <div className="card xs-mb-20 box-background">
                            <div className="card-body">
                                <h6 className="card-title">Tryggingataki</h6>
                                <hr/>
                                <div>
                                    <div className="case-label">Nafn</div> 
                                    {customerName}
                                </div>
                                <div>
                                    <div className="case-label">Kennitala</div> 
                                    {kennitala ? formatKennitala(kennitala) : '-'}
                                </div>
                                <div>
                                    <div className="case-label">Ráðgjaf</div> 
                                    {caseItem.AdvisorName || caseItem.AdvisorSSN || caseItem.AdvisorCode || '-'}
                                </div>
                            </div>
                        </div>

                        <div className="card xs-mb-20 box-background">
                            <div className="card-body">
                                <h6 className="card-title">Samningur</h6>
                                <hr/>
                                <div>
                                    <div className="case-label">Samningsnúmer</div> 
                                    {contractId || '-'}
                                </div>
                                <div>
                                   <div className="case-label">Samningsgerð</div> 
                                   {(contract && contract.ContractType) || '-'}
                                </div>
                                <div>
                                    <div className="case-label">Samningsstaða:</div> 
                                    {(contract && contract.ContractStatus) || '-'}
                                </div>
                                <div>
                                    <div className="case-label">Virkjun:</div> 
                                    {(contract && contract.StartDate) || '-'}
                                </div>
                            </div>
                        </div>
                        {contractFilesList && contractFilesList.length > 0 && 
                        <div className="card xs-mb-20 box-background">
                            <div className="card-body">
                                <h6 className="card-title">Viðhengi</h6>
                                <hr/>
                                {contractFilesList.map(file => 
                                    <div>
                                        <a href={`/api/v1/cases/case-details/${caseItem.CaseId}/attachment/${file.FileId}/${file.Name}.${file.FileType}`}>{file.Name}</a>
                                    </div>
                                )
                                }
                            </div>
                        </div>
                        }
                    </div>
                </div>
            </div>

            {this.state.showResolveDialog && (
                <div className="resolve-modal-overlay" role="dialog" aria-modal="true" aria-label="Staðfesta aðgerð">
                    <div className="resolve-modal">
                        <button type="button" className="resolve-modal-close" aria-label="Loka" onClick={this.closeResolveDialog}>×</button>
                        <div className="resolve-modal-message">{this.state.resolveDialogMessage || 'Þú ert að fara að ljúka máli'}</div>
                        <textarea
                            id="resolve-modal-textarea"
                            className="resolve-modal-textarea"
                            placeholder="Skráðu athugasemd hér..."
                            value={this.state.resolveDialogComment}
                            onChange={this.onResolveDialogCommentChange}
                            onKeyUp={(e) => e.code==='Escape' && this.closeResolveDialog()}
                        />
                        <button type="button" className="btn btn-primary" onClick={this.confirmResolveCase} disabled={this.state.isResolving}>
                            {this.state.isResolving &&
                                <i className="fa fa-spin fa-sync-alt xs-mr-10" />
                            }
                           Staðfesta og ljúka máli
                        </button>
                    </div>
                </div>
            )}
        </>);
    }
}

function mapStateToProps(state) {
    return {
        caseDetails: (state.caseDetails === undefined) ? {} : state.caseDetails,
    }
}

export default connectWithStore(store, AgentCaseDetails, mapStateToProps);
